import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ShieldCheck, FileText, Lock, ExternalLink } from "lucide-react";
import { LEGAL_DOCS } from "./LegalContent";
import type { LegalBlock, LegalDocId } from "./LegalContent";

// Two bottom sheets that share one look and one "fade up" entrance:
//
//   <LegalReaderSheet>      reads one of the documents (Terms, Privacy, About,
//                           Contact, FAQ, Report a Concern) inside the app.
//   <TermsAcceptanceSheet>  the "Review Our Terms" prompt: two rows that open
//                           the reader, then Cancel / I Agree in green.
//
// Both use the app's theme variables, so they must be rendered inside the
// themed root (anywhere in App's tree — NOT through a portal to <body>).

export const TERMS_GREEN = "#22C55E";
const GREEN_TEXT_ON = "#052E16"; // dark text on the green — far more legible than white

const FADE_UP = {
  initial: { opacity: 0, y: 36 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 24 },
  transition: { duration: 0.34, ease: [0.22, 1, 0.36, 1] as any },
};

// Make emails and plain web addresses tappable.
const LINK_RE =
  /([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}|odpc\.go\.ke|\+254\s?\d{3}\s?\d{6})/g;

function Linkified({ text }: { text: string }) {
  const parts = text.split(LINK_RE);
  return (
    <>
      {parts.map((part, i) => {
        if (i % 2 === 0) return <React.Fragment key={i}>{part}</React.Fragment>;
        let href = part;
        if (part.includes("@")) href = `mailto:${part}`;
        else if (part.startsWith("+")) href = `https://wa.me/${part.replace(/\D/g, "")}`;
        else href = `https://${part}`;
        return (
          <a
            key={i}
            href={href}
            target={part.includes("@") ? undefined : "_blank"}
            rel="noreferrer"
            className="font-semibold underline underline-offset-2 break-words"
            style={{ color: TERMS_GREEN }}
          >
            {part}
          </a>
        );
      })}
    </>
  );
}

function Block({ block }: { block: LegalBlock }) {
  switch (block.t) {
    case "h":
      return (
        <h3 className="mt-7 mb-2 text-[13px] font-extrabold uppercase tracking-wide text-[var(--text-primary)]">
          {block.text}
        </h3>
      );
    case "sub":
      return (
        <h4 className="mt-5 mb-1.5 text-sm font-bold text-[var(--text-primary)]">
          {block.text}
        </h4>
      );
    case "p":
      return (
        <p className="mb-3 text-sm leading-relaxed text-[var(--text-secondary)]">
          <Linkified text={block.text} />
        </p>
      );
    case "c":
      return (
        <p className="mb-3 text-sm leading-relaxed text-[var(--text-secondary)]">
          <strong className="font-bold text-[var(--text-primary)]">
            {block.title}
          </strong>{" "}
          <Linkified text={block.text} />
        </p>
      );
    case "ul":
      return (
        <ul className="mb-3 space-y-2">
          {block.items.map((item, i) => (
            <li
              key={i}
              className="flex gap-2.5 text-sm leading-relaxed text-[var(--text-secondary)]"
            >
              <span
                className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full"
                style={{ background: TERMS_GREEN }}
              />
              <span>
                <Linkified text={item} />
              </span>
            </li>
          ))}
        </ul>
      );
    case "faq":
      return (
        <div className="mb-4 rounded-2xl border border-[var(--border-soft)] bg-[var(--surface-soft)] p-4">
          <p className="mb-1.5 text-sm font-bold text-[var(--text-primary)]">
            {block.q}
          </p>
          <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
            <Linkified text={block.a} />
          </p>
        </div>
      );
  }
}

// ---------------------------------------------------------------------------
// Reader
// ---------------------------------------------------------------------------
export function LegalReaderSheet({
  docId,
  onClose,
  zIndex = 230,
}: {
  docId: LegalDocId | null;
  onClose: () => void;
  zIndex?: number;
}) {
  const doc = docId ? LEGAL_DOCS[docId] : null;

  // Escape closes it.
  React.useEffect(() => {
    if (!docId) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [docId, onClose]);

  return (
    <AnimatePresence>
      {doc && (
        <motion.div
          key="legal-reader"
          className="fixed inset-0 flex items-end justify-center"
          style={{ zIndex }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
        >
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={doc.title}
            {...FADE_UP}
            className="relative flex w-full max-w-md flex-col rounded-t-3xl border-t border-[var(--border-soft)] bg-[var(--bg-container)] text-[var(--text-primary)] shadow-2xl"
            style={{ height: "88vh" }}
          >
            <div className="mx-auto mt-2.5 h-1 w-10 shrink-0 rounded-full bg-[var(--border-soft)]" />
            <div className="flex shrink-0 items-start justify-between gap-3 px-6 pb-3 pt-4">
              <div>
                <h2 className="text-xl font-black">{doc.title}</h2>
                {doc.meta && (
                  <p className="mt-1 text-xs text-[var(--text-muted)]">
                    {doc.meta}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="-mr-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-soft-hover)] hover:text-[var(--text-primary)]"
              >
                <X size={18} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pb-10">
              {doc.blocks.map((b, i) => (
                <Block key={i} block={b} />
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------
// "Review Our Terms"
// ---------------------------------------------------------------------------
export function TermsAcceptanceSheet({
  isOpen,
  onAgree,
  onCancel,
}: {
  isOpen: boolean;
  onAgree: () => void;
  onCancel: () => void;
}) {
  const [readerDoc, setReaderDoc] = React.useState<LegalDocId | null>(null);

  // Start fresh each time it opens.
  React.useEffect(() => {
    if (!isOpen) setReaderDoc(null);
  }, [isOpen]);

  const rows: Array<{ id: LegalDocId; label: string; Icon: typeof FileText }> = [
    { id: "terms", label: "Terms of Use", Icon: FileText },
    { id: "privacy", label: "Privacy Policy", Icon: Lock },
  ];

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="terms-sheet"
            className="fixed inset-0 flex items-end justify-center"
            style={{ zIndex: 220 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            {/* No tap-to-dismiss: they have to choose Cancel or I Agree. */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label="Review our terms"
              {...FADE_UP}
              className="relative w-full max-w-md rounded-t-[2rem] border-t border-[var(--border-soft)] bg-[var(--bg-container)] px-6 pb-7 pt-3 text-[var(--text-primary)] shadow-2xl"
            >
              <div className="mx-auto mb-5 h-1 w-10 rounded-full bg-[var(--border-soft)]" />

              <div
                className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"
                style={{ background: `${TERMS_GREEN}26` }}
              >
                <ShieldCheck size={30} style={{ color: TERMS_GREEN }} />
              </div>

              <h2 className="text-center text-2xl font-black">
                Review Our Terms
              </h2>
              <p className="mx-auto mt-3 max-w-xs text-center text-sm leading-relaxed text-[var(--text-secondary)]">
                To start using INUA, please review and accept our Terms of Use
                and Privacy Policy.
              </p>

              <div className="mt-5 overflow-hidden rounded-2xl border border-[var(--border-soft)] bg-[var(--surface-soft)]">
                {rows.map(({ id, label, Icon }, i) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setReaderDoc(id)}
                    className={`flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-[var(--surface-soft-hover)] ${
                      i > 0 ? "border-t border-[var(--border-soft)]" : ""
                    }`}
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--bg-container)]">
                      <Icon size={18} className="text-[var(--text-secondary)]" />
                    </span>
                    <span className="flex-1 text-[15px] font-semibold">
                      {label}
                    </span>
                    <ExternalLink
                      size={16}
                      className="text-[var(--text-muted)]"
                    />
                  </button>
                ))}
              </div>

              <p className="mt-4 text-center text-xs leading-relaxed text-[var(--text-muted)]">
                By tapping &ldquo;I Agree&rdquo;, you confirm that you have read
                and consent to these agreements.
              </p>

              <div className="mt-5 flex gap-3">
                <button
                  type="button"
                  onClick={onCancel}
                  className="flex-1 rounded-2xl bg-[var(--surface-soft)] py-4 text-[15px] font-semibold text-[var(--text-primary)] transition-colors hover:bg-[var(--surface-soft-hover)] active:scale-[0.98]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onAgree}
                  className="flex-1 rounded-2xl py-4 text-[15px] font-bold transition-transform active:scale-[0.98]"
                  style={{
                    background: TERMS_GREEN,
                    color: GREEN_TEXT_ON,
                    boxShadow: `0 8px 24px -8px ${TERMS_GREEN}99`,
                  }}
                >
                  I Agree
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Opens above the terms sheet (z 230 > 220). */}
      <LegalReaderSheet docId={readerDoc} onClose={() => setReaderDoc(null)} />
    </>
  );
}
